<template>
  <div
    class="
      grid justify-content-center align-items-center
      vertical-container vertical-container-auth
      p-2
    "
  >
    <div class="box" v-if="loading">
      <div
        class="grid p-fluid justify-content-center align-items-center p-login-box"
      >
        <div class="col-10 md:col-10">
          <ProgressSpinner />
        </div>
      </div>
    </div>

    <div class="box" v-if="!loading">
      <form
        @submit.prevent="handleSubmit(!v$.$invalid)"
        @invalid.capture.prevent="handleInvalid()"
      >
        <div
          class="grid p-fluid justify-content-center align-items-center p-login-box"
        >
          <div class="col-10 md:col-10">
            <div class="text-center uppercase font-bold">
              <h1 class="p-text-RussoOne">emc</h1>
            </div>
          </div>

          <div class="col-10 md:col-10 p-3">
            <span
              class="p-label col-10 md:col-10"
              :class="{ 'p-error': v$.email.$invalid && submitted }"
            >
              Email
            </span>
            <InputGroup>
              <InputGroupAddon>
                <font-awesome-icon :icon="['fas', 'envelope']" />
              </InputGroupAddon>
              <InputText
                v-model="v$.email.$model"
                minLength="3"
                maxLength="100"
                required
                placeholder="Type email@enamor.pl"
                @keypress="emailKeypress($event)"
                :class="{ 'p-invalid': v$.email.$invalid && submitted }"
                :title="inputTitles.email"
              />
            </InputGroup>
            <Message
              v-if="v$.email.$invalid && submitted"
              size="small"
              severity="error"
              variant="simple"
            >
              Email is required, should be in range of 3 to 100 characters, with
              only small or/and capital letters, numbers and characters like
              "-", "_", ".". In addition, must be in format like
              "login@enamor.pl".
            </Message>
          </div>

          <div class="col-10 md:col-10 p-3">
            <span
              class="p-label"
              :class="{ 'p-error': v$.password.$invalid && submitted }"
            >
              Password
            </span>
            <InputGroup>
              <InputGroupAddon>
                <font-awesome-icon :icon="['fas', 'lock']" />
              </InputGroupAddon>
              <Password
                v-model="v$.password.$model"
                toggleMask
                :feedback="false"
                inputClass="p-password"
                placeholder="Type password"
                :class="{ 'p-invalid': v$.password.$invalid && submitted }"
                minLength="8"
                :title="inputTitles.password"
              ></Password>
            </InputGroup>
            <Message
              v-if="v$.password.$invalid && submitted"
              size="small"
              severity="error"
              variant="simple"
            >
              Password is required and must be minimum 8 characters long with at
              least one capital, small letter and number.
            </Message>
          </div>

          <div class="col-10 md:col-10">
            <div class="col-12">
              <Button type="submit" class="w-full">
                <font-awesome-icon
                  class="mr-2"
                  :icon="['fas', 'sign-in-alt']"
                />
                <span class="ml-2 font-bold">Sign In</span>
              </Button>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeMount, reactive, ref, watch } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { useToast } from "primevue/usetoast";
import {
  createToast,
  emailKeypress,
  validationAuthEmc,
  validationRegExEmail,
  validationRegExPass
} from "../functions/utils";
import { maxLength, minLength, required } from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";

export default {
  name: "EmcAuth",
  setup() {
    const toast = useToast();
    const store = useStore();
    const router = useRouter();

    const errorMsg = computed(() => store.getters["auth/authErrorMsg"]);
    const loggedIn = computed(() => store.getters["auth/userIsLogged"]);
    const permissions = computed ( () => store.getters["auth/permissions"]);
    const userIsAdmin = computed ( () => store.getters["auth/userIsAdmin"]);

    const loading = ref(false);

    const state = reactive({
      email: "",
      password: "",
    });

    const inputTitles = {
      email: "Email is required, should be in range of 3 to 100 characters, \n" +
              "with only small or/and capital letters, numbers and characters like - _ . \n" +
              "In addition, must be in format like 'login@enamor.pl'.",
      password: "Password is required and must be minimum 8 characters long with at least one capital, \n" +
                "small letter and number."
    }

    const rules = {
      email: {
        required,
        minLength: minLength(13),
        maxLength: maxLength(100),
        valid: validationRegExEmail,
        validApi: validationAuthEmc(errorMsg),
      },
      password: {
        required,
        minLength: minLength(8),
        valid: validationRegExPass,
        validApi: validationAuthEmc(errorMsg),
      },
    };

    const v$ = useVuelidate(rules, state);

    const submitted = ref(false);

    const handleSubmit = (isFormValid) => {
      submitted.value = true;

      if (isFormValid) {
        //FORMING THE VOID (REQUEST)
        const user = {
          email: state.email && state.email,
          password: state.password && state.password,
        };

        store
          .dispatch("auth/login", user)
          .then(
            (res) => {
              loading.value = true;
              loggedIn.value ? redirectToDefaultPage() : null;
              createToast(
                toast,
                "success",
                res.message ? res.message : "Login successful",
                ""
              );
            },
            (error) => {
              loading.value = false;
              console.error(error);
            }
          )
          .catch(() => {
            loading.value = false;
            createToast(toast, "error", "Error!", `${errorMsg.value}`);
          });
      }
    };

    const handleInvalid = () => {
      submitted.value = true;
    };

    watch(
      () => state.email,
      (emailValue, prevEmailValue) => {
        if (emailValue !== prevEmailValue && errorMsg.value !== "") {
          store.commit("auth/updateAuthApiErrorMsg", "");
        }
      }
    );

    watch(
      () => state.password,
      (passwordValue, prevPasswordValue) => {
        if (passwordValue !== prevPasswordValue && errorMsg.value !== "") {
          store.commit("auth/updateAuthApiErrorMsg", "");
        }
      }
    );

    onBeforeMount(() => {
      if (loggedIn.value) {
        redirectToDefaultPage();
      }
    });

    const redirectToDefaultPage = () => {
      if (userIsAdmin.value || permissions.value.map((x) => x.key).includes("view-service")) {
        router.push("/service/list");
      } else if (userIsAdmin.value || permissions.value.map((x) => x.key).includes("view-kpi-standard")) {
        router.push("/kpi/kpiDashboard");
      } else if (permissions.value.map((x) => x.key).includes("view-vessels")) {
        router.push("/ship/listShips");
      } else {
        logOut();
      }
    };
    const logOut = () => {
      store.dispatch("auth/logout").then(
        (res) => {
          window.sessionStorage.removeItem("timePassed");
          router.push("/login");
          createToast(
            toast,
            "info",
            res.message ? res.message : "Logged out!",
            ""
          );
        },
        (error) => {
          createToast(
            toast,
            "error",
            "Not logged",
            error.message ? error.message : "Not logged out"
          );
        }
      );
    };

    return {
      v$,
      loggedIn,
      loading,
      submitted,
      inputTitles,
      emailKeypress,
      handleSubmit,
      handleInvalid,
    };
  },
};
</script>

<style lang="scss" scoped>
.box {
  background-color: var(--surface-e);
  text-align: center;
  padding-top: 1rem;
  padding-bottom: 1rem;
  border-radius: 4px;
  box-shadow: 0 2px 1px -1px rgba(0, 0, 0, 0.2), 0 1px 1px 0 rgba(0, 0, 0, 0.14),
    0 1px 3px 0 rgba(0, 0, 0, 0.12);
}

.p-login-box {
  width: 40vw;
  min-height: 40vh;
}

.p-password {
  width: 100% !important;
}

.vertical-container-auth {
  height: 98vh !important;
}

.p-label {
  font-size: 0.85rem;
  font-style: italic;
  padding: 0.25rem;
  display: flex;
  flex: 1;
  justify-content: flex-start;
}

.p-error-auth {
  white-space: nowrap;
  word-wrap: break-word;
}

.p-toast-message-text {
  white-space: normal;
  max-width: 300px;
  overflow-wrap: break-word;
}
</style>
